import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import './i18n/index';
import Sidebar from './components/Sidebar';
import SupportChat from './components/SupportChat';
import { useWebSocket } from './hooks/useWebSocket';

// ── React Query client ──────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// ── Lazy-loaded pages ───────────────────────────────────────────────────────
const Landing      = lazy(() => import('./pages/Landing'));
const Login        = lazy(() => import('./pages/auth/Login'));
const Register     = lazy(() => import('./pages/auth/Register'));
const VerifyEmail  = lazy(() => import('./pages/auth/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword  = lazy(() => import('./pages/auth/ResetPassword'));
const SalesDemo      = lazy(() => import('./pages/auth/SalesDemo'));
const Pair           = lazy(() => import('./pages/Pair'));
const Onboarding   = lazy(() => import('./pages/onboarding/Onboarding'));
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const CamerasPage   = lazy(() => import('./pages/CamerasPage'));
const CamerasManage = lazy(() => import('./pages/CamerasManage'));
const SetupWizard   = lazy(() => import('./pages/SetupWizard'));
const DemoCenter    = lazy(() => import('./pages/DemoCenter'));
const ZoneEditor    = lazy(() => import('./pages/ZoneEditorPage'));
const HighValueCounter = lazy(() => import('./pages/HighValueCounterSetup'));
const RelaySetup     = lazy(() => import('./pages/RelaySetup'));
const CameraView   = lazy(() => import('./pages/CameraView'));
const IncidentsPage = lazy(() => import('./pages/IncidentsPage'));
const WatchlistPage = lazy(() => import('./pages/WatchlistPage'));
const StaffFacesPage = lazy(() => import('./pages/StaffFacesPage'));
const PeopleCountPage = lazy(() => import('./pages/PeopleCountPage'));
const StoreDetail  = lazy(() => import('./pages/StoreDetail'));
const StoresPage   = lazy(() => import('./pages/StoresPage'));
const MultiStoreWall = lazy(() => import('./pages/MultiStoreWall'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const AgentStatusPage = lazy(() => import('./pages/AgentStatusPage'));
const AccountPage    = lazy(() => import('./pages/AccountPage'));
const HowItWorks   = lazy(() => import('./pages/HowItWorks'));
const FAQ          = lazy(() => import('./pages/FAQ'));
const HelpCenter   = lazy(() => import('./pages/HelpCenter'));
const HealthCheck  = lazy(() => import('./pages/HealthCheck'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const PartnerAdminPage = lazy(() => import('./pages/admin/PartnerAdminPage'));
const PartnerLoginPage = lazy(() => import('./pages/partner/PartnerLoginPage'));
const PartnerDashboardPage = lazy(() => import('./pages/partner/PartnerDashboardPage'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// ── Auth helpers ─────────────────────────────────────────────────────────────
function isAuthenticated() {
  return !!localStorage.getItem('vantag_token');
}

function isSuperAdmin() {
  return localStorage.getItem('vantag_is_super_admin') === 'true';
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
}

/** Guard for /admin/* — must be authenticated AND super-admin */
function AdminRoute({ children }: { children: React.ReactNode }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!isSuperAdmin()) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Guard for /partner/dashboard — separate, isolated partner session token. */
function PartnerRoute({ children }: { children: React.ReactNode }) {
  return localStorage.getItem('vantag_partner_token')
    ? <>{children}</>
    : <Navigate to="/partner/login" replace />;
}

// ── Loading spinner ─────────────────────────────────────────────────────────
function Loading() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ── App shell with sidebar (authenticated pages) ────────────────────────────
function AppLayout() {
  // Initialise the authenticated WebSocket once for all dashboard pages.
  // Live door status now arrives over this connection (door_state messages).
  useWebSocket();
  return (
    <div className="flex min-h-screen bg-vantag-dark">
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

// ── Root App ────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' },
            success: { iconTheme: { primary: '#8b5cf6', secondary: '#fff' } },
          }}
        />
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/sales-demo" element={<SalesDemo />} />
            <Route path="/pair" element={<Pair />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Onboarding — auth required, no sidebar */}
            <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />

            {/* Admin panel — super-admin only */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/partners" element={<AdminRoute><PartnerAdminPage /></AdminRoute>} />
            <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

            {/* Partner Portal — dealers/distributors/freelancers, isolated auth */}
            <Route path="/partner/login" element={<PartnerLoginPage />} />
            <Route path="/partner/dashboard" element={<PartnerRoute><PartnerDashboardPage /></PartnerRoute>} />
            <Route path="/partner" element={<Navigate to="/partner/login" replace />} />

            {/* App shell — auth required, with sidebar + WebSocket */}
            <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
              <Route path="/dashboard"              element={<Dashboard />} />
              <Route path="/cameras"               element={<CamerasPage />} />
              <Route path="/cameras/manage"        element={<CamerasManage />} />
              <Route path="/demo"                  element={<DemoCenter />} />
              <Route path="/zone-editor"           element={<ZoneEditor />} />
              <Route path="/high-value-counter"     element={<HighValueCounter />} />
              <Route path="/relay-setup"            element={<RelaySetup />} />
              <Route path="/cameras/:storeId/:cameraId" element={<CameraView />} />
              <Route path="/incidents"  element={<IncidentsPage />} />
              <Route path="/watchlist"  element={<WatchlistPage />} />
              <Route path="/staff-faces" element={<StaffFacesPage />} />
              <Route path="/people-count" element={<PeopleCountPage />} />
              <Route path="/stores"     element={<StoresPage />} />
              <Route path="/stores/:id" element={<StoreDetail />} />
              <Route path="/wall"       element={<MultiStoreWall />} />
              <Route path="/download"   element={<DownloadPage />} />
              <Route path="/agent-status" element={<AgentStatusPage />} />
              <Route path="/account"     element={<AccountPage />} />
              <Route path="/setup"       element={<SetupWizard />} />
              <Route path="/help"       element={<HelpCenter />} />
              <Route path="/help/how-it-works" element={<HowItWorks embedded />} />
              <Route path="/help/faq"   element={<FAQ embedded />} />
              <Route path="/health-check" element={<HealthCheck />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <SupportChat />
      </BrowserRouter>
    </QueryClientProvider>
    </HelmetProvider>
  );
}
